import { useCallback, useMemo, useRef, useState } from 'react'
import { startOfMonth, endOfMonth, differenceInCalendarDays } from 'date-fns'
import {
  DayPicker,
  DateRange,
  DayButtonProps,
  Matcher,
  Modifiers,
} from 'react-day-picker'

import ArrowRightSlim from 'components/icons/ArrowRightSlim'

import { Wrapper, FlippedChevronWrap } from './styled'
import DayCell from './DayCell'

// rdp v9 calls `components.Chevron` inside each nav button with an
// `orientation` prop. Swap rdp's default for the brand ArrowRightSlim
// SVG; for the prev button we re-use the same icon flipped horizontally
// (there's no dedicated ArrowLeftSlim) so the two arrows stay visually
// matched.
type ChevronProps = { orientation?: 'left' | 'right' | 'up' | 'down' }
const Chevron = ({ orientation }: ChevronProps) =>
  orientation === 'right' ? (
    <ArrowRightSlim />
  ) : (
    <FlippedChevronWrap>
      <ArrowRightSlim />
    </FlippedChevronWrap>
  )

export type DayModifiers = Modifiers

export type RenderDay = (date: Date, modifiers: DayModifiers) => React.ReactNode

export type DateRangeCalendarProps = {
  startDate: Date | null
  endDate: Date | null
  onChange: (startDate: Date | null, endDate: Date | null) => void
  numberOfMonths?: number
  minDate?: Date
  maxDate?: Date
  isDayBlocked?: (date: Date) => boolean
  isOutsideRange?: (date: Date) => boolean
  // A value of 1 (the default) means end must be at least one day after start;
  // pass 0 to allow same-day selection.
  minimumNights?: number
  onMonthChange?: (visibleMonth: Date) => void
  // Fires when the mouse enters a day cell. Used by callers that want
  // hover-preview range highlighting (the booking calendar needs this;
  // the search filters don't).
  onDayMouseEnter?: (date: Date) => void
  // Fires when the mouse leaves a day cell. Pair with `onDayMouseEnter`
  // to clear hover-preview state when the cursor leaves the grid.
  onDayMouseLeave?: (date: Date) => void
  renderDay?: RenderDay
  daySize?: number
  // Horizontal gap between months in multi-month view. Accepts any CSS length.
  monthsGap?: string
  firstDayOfWeek?: 0 | 1
  defaultMonth?: Date
  excludeDisabled?: boolean
  // Content rendered below the calendar grid inside the same wrapper element,
  // so it inherits theme + rdp CSS variables. Use this for per-surface legends,
  // discount info, or action rows that should travel with the calendar.
  renderBelow?: React.ReactNode
  className?: string
}

const buildDisabled = ({
  minDate,
  maxDate,
  isDayBlocked,
  isOutsideRange,
}: Pick<
  DateRangeCalendarProps,
  'minDate' | 'maxDate' | 'isDayBlocked' | 'isOutsideRange'
>): Matcher[] => {
  const matchers: Matcher[] = []
  if (minDate) matchers.push({ before: minDate })
  if (maxDate) matchers.push({ after: maxDate })
  if (isDayBlocked) matchers.push((d: Date) => isDayBlocked(d))
  if (isOutsideRange) matchers.push((d: Date) => isOutsideRange(d))
  return matchers
}

const DateRangeCalendar = ({
  startDate,
  endDate,
  onChange,
  numberOfMonths = 1,
  minDate,
  maxDate,
  isDayBlocked,
  isOutsideRange,
  minimumNights = 1,
  onMonthChange,
  onDayMouseEnter,
  onDayMouseLeave,
  renderDay,
  daySize,
  monthsGap,
  firstDayOfWeek = 1,
  defaultMonth,
  excludeDisabled,
  renderBelow,
  className,
}: DateRangeCalendarProps) => {
  const selected: DateRange | undefined = useMemo(() => {
    // rdp range mode only accepts ranges with a defined `from`. An
    // `endDate`-without-`startDate` configuration would produce an invalid
    // selection, so treat it the same as an empty range.
    if (!startDate) return undefined
    return {
      from: startDate,
      to: endDate ?? undefined,
    }
  }, [startDate, endDate])

  const disabled = useMemo(
    () => buildDisabled({ minDate, maxDate, isDayBlocked, isOutsideRange }),
    [minDate, maxDate, isDayBlocked, isOutsideRange]
  )

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      onChange(range?.from ?? null, range?.to ?? null)
    },
    [onChange]
  )

  const handleMonthChange = useCallback(
    (month: Date) => {
      onMonthChange?.(month)
    },
    [onMonthChange]
  )

  // Track the currently-hovered day so the wrapper can paint a "hover
  // preview" range between the selected start and the cursor. Every
  // consumer gets the preview automatically — no per-surface plumbing.
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)

  const handleDayMouseEnter = useCallback(
    (date: Date) => {
      setHoveredDate(date)
      onDayMouseEnter?.(date)
    },
    [onDayMouseEnter]
  )

  const handleDayMouseLeave = useCallback(
    (date: Date) => {
      onDayMouseLeave?.(date)
    },
    [onDayMouseLeave]
  )

  const handleWrapperMouseLeave = useCallback(() => {
    // Clear the hover preview when the cursor leaves the grid entirely so
    // the highlight doesn't get "stuck" pointing at the last-hovered cell.
    setHoveredDate(null)
  }, [])

  const previewModifiers = useMemo(() => {
    if (!startDate || endDate || !hoveredDate) return undefined
    const start = startDate
    const hover = hoveredDate
    return {
      previewMiddle: (date: Date) =>
        differenceInCalendarDays(date, start) > 0 &&
        differenceInCalendarDays(date, hover) <= 0,
    }
  }, [startDate, endDate, hoveredDate])

  const modifiersClassNames = useMemo(
    () => ({ previewMiddle: 'rdp-preview_middle' }),
    []
  )

  // Keep the custom DayButton's component identity stable across renders.
  // rdp v9 unmounts and remounts cells when `components.DayButton` is a
  // different reference, which races with userEvent's click sequence (the
  // mouseenter handler can trigger a re-render mid-click). Reading the
  // latest `renderDay` via a ref lets callers pass a fresh closure
  // (e.g. one that captures hover state for a preview range) without
  // destabilising the cell tree.
  const renderDayRef = useRef(renderDay)
  renderDayRef.current = renderDay

  const RenderDayButton = useCallback(
    (props: DayButtonProps) => (
      <DayCell {...props} renderDay={renderDayRef.current!} />
    ),
    []
  )

  const components = useMemo(() => {
    if (!renderDay) return { Chevron }
    return { DayButton: RenderDayButton, Chevron }
  }, [renderDay, RenderDayButton])

  return (
    <Wrapper
      className={className}
      $daySize={daySize}
      $monthsGap={monthsGap}
      onMouseLeave={handleWrapperMouseLeave}
    >
      <DayPicker
        mode="range"
        selected={selected}
        onSelect={handleSelect}
        numberOfMonths={numberOfMonths}
        weekStartsOn={firstDayOfWeek}
        disabled={disabled}
        excludeDisabled={excludeDisabled}
        min={minimumNights > 0 ? minimumNights : undefined}
        defaultMonth={defaultMonth}
        startMonth={minDate ? startOfMonth(minDate) : undefined}
        endMonth={maxDate ? endOfMonth(maxDate) : undefined}
        onMonthChange={onMonthChange ? handleMonthChange : undefined}
        onDayMouseEnter={handleDayMouseEnter}
        onDayMouseLeave={onDayMouseLeave ? handleDayMouseLeave : undefined}
        components={components}
        modifiers={previewModifiers}
        modifiersClassNames={modifiersClassNames}
        navLayout="around"
        // rdp v9's animate prop slides the month caption + grid on
        // navigation, which reads gracefully but keeps the outgoing
        // month's snapshot in the DOM mid-transition. The jest DOM
        // can't tell the snapshot apart from the live month, so the
        // existing month-navigation assertions need it off in tests.
        animate={process.env.NODE_ENV !== 'test'}
        // rdp's resetOnSelect bypasses addToRange on the first click,
        // emitting `{ from, to: undefined }` regardless of `min`. That
        // forces availability surfaces (minimumNights=0, single-date
        // semantics) into a two-click UX. Disable it when min is 0 so
        // addToRange's auto-complete branch fires and a single click
        // yields the same-day range those surfaces expect.
        resetOnSelect={minimumNights > 0}
      />
      {renderBelow}
    </Wrapper>
  )
}

export default DateRangeCalendar
